import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import type { ProjectSelection, Server } from "@/lib/api";
import { IntegrationLogo } from "@/components/IntegrationLogo";
import { cn } from "@/lib/utils";

// PickerResult is what the picker emits on every change: the selection to
// send to the backend (undefined when every connected server is fully
// checked → backend stores the canonical nil="all"), and isEmpty so the
// dialog can disable submit when nothing is checked.
export interface PickerResult {
  selection: ProjectSelection | undefined;
  isEmpty: boolean;
}

// CheckedMap holds, per checked server name, the set of its checked tools.
// A server absent from the map is excluded; present with all its connected
// tools = full (all_tools), present with a subset = partial (frozen list).
type CheckedMap = Map<string, Set<string>>;

// A "stale" reference from a pre-filled selection: a selected server no
// longer connected, or a selected tool no longer present upstream. We show
// these greyed with a red note but never emit them.
interface StaleRef {
  server: string;
  tools: string[]; // empty ⇒ the whole server is gone
  serverGone: boolean;
}

function fullyChecked(server: Server, checked: Set<string> | undefined): boolean {
  if (!checked) return false;
  return server.tools.length > 0 && server.tools.every((t) => checked.has(t));
}

function partiallyChecked(server: Server, checked: Set<string> | undefined): boolean {
  if (!checked || checked.size === 0) return false;
  return !fullyChecked(server, checked);
}

// initialChecked builds the starting CheckedMap from a pre-fill selection.
// null/undefined ⇒ everything checked, UNLESS defaultEmpty (opt-in mode: a
// fresh "from scratch" agent starts with nothing selected, so the user
// deliberately grants each integration — least privilege). Tools no longer
// present upstream are dropped from the live map (they surface as stale).
function initialChecked(
  servers: Server[],
  initial: ProjectSelection | null | undefined,
  defaultEmpty: boolean,
): CheckedMap {
  const map: CheckedMap = new Map();
  if (initial == null) {
    if (defaultEmpty) return map; // opt-in: nothing checked until the user picks
    for (const s of servers) map.set(s.name, new Set(s.tools));
    return map;
  }
  const byName = new Map(servers.map((s) => [s.name, s]));
  for (const sel of initial.servers) {
    const live = byName.get(sel.name);
    if (!live) continue; // disconnected/removed → handled as stale, not checked
    if (sel.all_tools) {
      map.set(sel.name, new Set(live.tools));
    } else {
      const present = (sel.tools ?? []).filter((t) => live.tools.includes(t));
      map.set(sel.name, new Set(present));
    }
  }
  return map;
}

// staleRefs computes selection entries that can no longer be honored, so the
// dialog can show them explicitly (greyed + red note) instead of silently
// dropping them.
function staleRefs(servers: Server[], initial: ProjectSelection | null | undefined): StaleRef[] {
  if (initial == null) return [];
  const byName = new Map(servers.map((s) => [s.name, s]));
  const out: StaleRef[] = [];
  for (const sel of initial.servers) {
    const live = byName.get(sel.name);
    if (!live) {
      out.push({ server: sel.name, tools: [], serverGone: true });
      continue;
    }
    if (!sel.all_tools) {
      const gone = (sel.tools ?? []).filter((t) => !live.tools.includes(t));
      if (gone.length > 0) out.push({ server: sel.name, tools: gone, serverGone: false });
    }
  }
  return out;
}

// computeResult turns the live CheckedMap into the emitted PickerResult.
// Rule: if EVERY connected server is fully checked → selection is undefined
// (canonical "all"). Otherwise build the explicit servers array.
function computeResult(servers: Server[], checked: CheckedMap): PickerResult {
  const isEmpty = servers.every((s) => {
    const set = checked.get(s.name);
    return !set || set.size === 0;
  });

  const allFull = servers.length > 0 && servers.every((s) => fullyChecked(s, checked.get(s.name)));
  if (allFull) return { selection: undefined, isEmpty: false };

  const out: ProjectSelection["servers"] = [];
  for (const s of servers) {
    const set = checked.get(s.name);
    if (!set || set.size === 0) continue;
    if (fullyChecked(s, set)) {
      out.push({ name: s.name, all_tools: true });
    } else {
      out.push({ name: s.name, all_tools: false, tools: s.tools.filter((t) => set.has(t)) });
    }
  }
  return { selection: { servers: out }, isEmpty };
}

// Native checkbox styled to the app's tokens — there's no Checkbox primitive
// in ui/, and adding a Radix dep for this would be overkill. indeterminate is
// a DOM-only prop, so we set it via ref.
function Check({
  checked,
  indeterminate,
  onChange,
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  "aria-label": string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      className="h-4 w-4 shrink-0 cursor-pointer rounded border-input text-primary accent-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
  );
}

// ServerToolPicker renders connected servers as expandable rows of tools with
// tri-state-ish checkboxes. It owns the checkbox state and reports the derived
// selection up via onChange (selection + isEmpty). Pre-fill via `initial`
// (null ⇒ all checked, the default).
export function ServerToolPicker({
  servers,
  initial,
  defaultEmpty = false,
  onChange,
}: {
  servers: Server[];
  initial?: ProjectSelection | null;
  // defaultEmpty: with no pre-fill, start with nothing checked (opt-in). Used
  // by the "from scratch" create flow so a new agent isn't auto-granted every
  // connected integration.
  defaultEmpty?: boolean;
  onChange: (r: PickerResult) => void;
}) {
  const [checked, setChecked] = useState<CheckedMap>(() => initialChecked(servers, initial, defaultEmpty));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Re-seed when the universe or pre-fill changes (e.g. the edit dialog loads
  // its selection after the picker first mounts).
  useEffect(() => {
    setChecked(initialChecked(servers, initial, defaultEmpty));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servers, initial, defaultEmpty]);

  const stale = useMemo(() => staleRefs(servers, initial), [servers, initial]);

  // Report the derived result on every state change (and on first mount).
  useEffect(() => {
    onChange(computeResult(servers, checked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, servers]);

  function update(mut: (next: CheckedMap) => void) {
    setChecked((prev) => {
      const next = new Map<string, Set<string>>();
      for (const [k, v] of prev) next.set(k, new Set(v));
      mut(next);
      return next;
    });
  }

  function toggleServer(s: Server) {
    update((next) => {
      if (fullyChecked(s, next.get(s.name))) {
        next.delete(s.name); // full → unchecked
      } else {
        next.set(s.name, new Set(s.tools)); // unchecked/partial → full
      }
    });
  }

  function toggleTool(s: Server, tool: string) {
    update((next) => {
      const set = new Set(next.get(s.name) ?? []);
      if (set.has(tool)) set.delete(tool);
      else set.add(tool);
      if (set.size === 0) next.delete(s.name);
      else next.set(s.name, set);
    });
  }

  function toggleExpand(name: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function selectAll() {
    update((next) => {
      for (const s of servers) next.set(s.name, new Set(s.tools));
    });
  }
  function clearAll() {
    update((next) => {
      for (const s of servers) next.delete(s.name);
    });
  }

  if (servers.length === 0) {
    return <p className="text-sm text-muted-foreground">Connect an integration first to choose its tools.</p>;
  }

  const selectedCount = servers.filter((s) => (checked.get(s.name)?.size ?? 0) > 0).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {selectedCount} of {servers.length} integrations selected
        </span>
        <div className="flex gap-3">
          <button type="button" onClick={selectAll} className="font-medium text-primary hover:underline">
            Select all
          </button>
          <button type="button" onClick={clearAll} className="text-muted-foreground hover:underline">
            Clear
          </button>
        </div>
      </div>
      <ul className="space-y-1.5">
        {servers.map((s) => {
          const set = checked.get(s.name);
          const full = fullyChecked(s, set);
          const partial = partiallyChecked(s, set);
          const isOpen = expanded.has(s.name);
          return (
            <li key={s.name} className="rounded-md border">
              <div className="flex items-center gap-2 px-3 py-2">
                <Check
                  checked={full}
                  indeterminate={partial}
                  onChange={() => toggleServer(s)}
                  aria-label={`Select all tools of ${s.name}`}
                />
                <IntegrationLogo name={s.name} className="h-6 w-6" />
                <span className="flex-1 truncate text-sm font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground">
                  {set ? set.size : 0}/{s.tools.length}
                </span>
                {s.tools.length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleExpand(s.name)}
                    aria-label={isOpen ? `Collapse ${s.name}` : `Expand ${s.name}`}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="space-y-1 border-t px-3 py-2">
                  {s.tools.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">No tools.</p>
                  ) : (
                    s.tools.map((t) => (
                      <label key={t} className="flex cursor-pointer items-center gap-2 py-0.5">
                        <Check
                          checked={!!set?.has(t)}
                          onChange={() => toggleTool(s, t)}
                          aria-label={`Select ${t}`}
                        />
                        <span className="truncate font-mono text-xs">{t}</span>
                      </label>
                    ))
                  )}
                  {partial && (
                    <p className="pt-1 text-xs text-warning">
                      Only these tools are included; tools added upstream later won't appear automatically.
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {stale.length > 0 && (
        <ul className="space-y-1.5">
          {stale.map((r) => (
            <li
              key={r.server}
              className="rounded-md border border-dashed border-destructive/30 px-3 py-2 opacity-70"
            >
              <div className="flex items-center gap-2">
                <span className="flex-1 truncate text-sm font-medium text-muted-foreground line-through">
                  {r.server}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-destructive">
                {r.serverGone
                  ? "This integration is no longer connected and was dropped from the selection."
                  : `These tools are no longer available and were dropped: ${r.tools.join(", ")}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// expandSelection resolves a selection to the concrete server→tools map it
// grants (a nil selection means "all connected servers + all their tools").
function expandSelection(
  sel: ProjectSelection | null | undefined,
  servers: Server[],
): Map<string, Set<string>> {
  const byName = new Map(servers.map((s) => [s.name, s]));
  const out = new Map<string, Set<string>>();
  if (sel == null) {
    for (const s of servers) out.set(s.name, new Set(s.tools));
    return out;
  }
  for (const e of sel.servers) {
    const live = byName.get(e.name);
    if (e.all_tools) out.set(e.name, new Set(live ? live.tools : []));
    else out.set(e.name, new Set(e.tools ?? []));
  }
  return out;
}

// droppedFromSelection lists "server" or "server / tool" entries present in
// the old selection but absent from the new one, so removals are explicit.
export function droppedFromSelection(
  oldSel: ProjectSelection | null | undefined,
  newSel: ProjectSelection | null | undefined,
  servers: Server[],
): string[] {
  const before = expandSelection(oldSel, servers);
  const after = expandSelection(newSel, servers);
  const out: string[] = [];
  for (const [server, tools] of before) {
    const stillThere = after.get(server);
    if (!stillThere) {
      out.push(server);
      continue;
    }
    for (const t of tools) {
      if (!stillThere.has(t)) out.push(`${server} / ${t}`);
    }
  }
  return out;
}
